import { Button, Grid, Image, IOption, Input } from '@design-systems/canvas-ui'
import { useState } from 'react'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import Icon, { CloseIcon } from '@design-systems/react-icons'
import { Link, useOutletContext } from 'react-router-dom'
import { Brand } from './brands'
import { getTrimmedBrandName } from './helpers'
import { useLogClick } from 'utils/analytics'

interface BrandCategoryOption {
    id: string
    value: string
    label: string
    isDisabled: boolean
}

interface OutletContext {
    data: Brand[]
    handleBrandSelect: (brand: Brand) => void
}

export const BrandsAll = () => {
    const { data, handleBrandSelect } = useOutletContext<OutletContext>()
    const [selectedCategory, setSelectedCategory] = useState<string>('All')
    const [searchTerm, setSearchTerm] = useState<string>('')
    const { t, currentLocation } = useEssentials()
    const { brands } = TRANSLATION_KEYS
    const { logClick } = useLogClick('brands all')
    const getUniqueBrandCategories = (data: Brand[]): BrandCategoryOption[] => {
        if (!Array.isArray(data)) return []

        const categories = data
            .map((item) => item.brand_category)
            .filter((category) => category !== 'All')

        const uniqueCategories = Array.from(new Set(categories))

        return uniqueCategories.map((category) => ({
            value: category,
            id: category,
            label: category,
            isDisabled: false,
        }))
    }

    const brandCategoryOptions: BrandCategoryOption[] = data
        ? getUniqueBrandCategories(data)
        : []

    const filteredBrands = data?.filter((brand: Brand) => {
        const matchesSearch = brand.brand_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        const matchesCategory =
            selectedCategory === 'All' ||
            brand.brand_category === selectedCategory
        return matchesSearch && matchesCategory
    })

    return (
        <Grid.Container>
            <Grid.Item
                size={{
                    xs: 12,
                }}
                className="hc8-pv-3x"
            >
                <Input.Text
                    placeholder={t(brands.search)}
                    value={searchTerm}
                    className="location-search-input"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchTerm(event.target.value)
                    }
                />
                {searchTerm && (
                    <Button
                        isIconOnly
                        id="search-button"
                        aria-label={t(brands.clearSearch)}
                        onClick={() => {
                            logClick('clear brand search')
                            setSearchTerm('')
                        }}
                        variant="ghost"
                    >
                        <Icon svg={CloseIcon} aria-hidden />
                    </Button>
                )}
            </Grid.Item>
            <Grid.Item
                size={{
                    xs: 12,
                }}
                className="hc8-pb-3x"
            >
                <Input.Select
                    id="brand_category"
                    value={selectedCategory}
                    options={[
                        {
                            id: 'All',
                            value: 'All',
                            label: 'All',
                            isDisabled: false,
                        },
                        ...brandCategoryOptions,
                    ]}
                    placeholder="Select an option"
                    onChange={(option: IOption | undefined) => {
                        setSelectedCategory(String(option?.value ?? 'All'))
                    }}
                />
            </Grid.Item>
            {filteredBrands?.map((brand: Brand) => (
                <Grid.Item
                    key={brand.brand_id}
                    onClick={() => handleBrandSelect(brand)}
                    className="hc8-pb-none"
                    size={{
                        xs: 6,
                        sm: 3,
                        lg: 2,
                    }}
                >
                    <Link
                        to={`/brands/${getTrimmedBrandName(brand.brand_name)}?locationId=${currentLocation}`}
                        onClick={(e) => {
                            e.preventDefault()
                            handleBrandSelect(brand)
                        }}
                        className="brand-image-link"
                    >
                        <Image
                            src={encodeURI(brand.image_url).replace(
                                /'/g,
                                '%27',
                            )}
                            alt={brand.brand_name}
                            width="100%"
                        />
                    </Link>
                </Grid.Item>
            ))}
        </Grid.Container>
    )
}
